import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import Popup from "../Popup/Popup.jsx";
import InputComponent from "../../components/DynamicInput/DynamicInput";
import {
  useLazyGetSchoolEditStateQuery,
  useUpdateSchoolMutation,
} from "../../../store/schools/schoolsMutation";
import {
  useGetCountriesMutation,
  useGetStatesMutation,
  useGetDistrictsMutation,
  useGetCitiesMutation,
  useGetCityMutation,
  useValidatePincodeMutation,
} from "../../../store/geo/countryApi";
import {
  mapGeoOptions,
  findGeoOptionByName,
  findDistrictForCityId,
  getPincodeValidationError,
  extractApiErrorMessage,
} from "../../utils/geoUtils";
import {
  BASIC_DETAIL_FIELDS,
  SCHOOL_EDIT_STATES,
} from "../../../constants/schoolEditConstants";
import {
  canUserEditSchool,
  isEditButtonDisabled,
  isFieldEditable,
  getEditableFields,
  buildUpdateSchoolPayload,
  validateSchoolEditForm,
  createFormDataFromSchool,
  hasFormChanges,
  validateEmailField,
  validateUdiseCode,
  getPincodeConfig,
  UDISE_CODE_LENGTH,
} from "../../utils/schoolEditUtils";
import loaderGif from "../../../assets/gifs/loader.gif";
import closeIcon from "../../../assets/images/close_icon.svg";
import "./SchoolDetailsPopup.css";

const NotificationPortal = ({ children }) =>
  ReactDOM.createPortal(children, document.body);

const notificationStyle = {
  position: "fixed",
  bottom: "0",
  right: "0",
  backgroundColor: "#fff",
  padding: " 30px 40px 30px 15px",
  borderRadius: "8px 0 0 0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  zIndex: 1002,
  maxWidth: "400px",
};

const notificationCloseStyle = {
  position: "absolute",
  top: "10px",
  right: "10px",
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "0",
};

const GEO_FIELD_ID_MAP = {
  country: "country_id",
  state: "state_id",
  district: "district_id",
  city: "city_id",
};

const EMPTY_GEO_FIELDS = {
  state_id: "",
  state: "",
  district_id: "",
  district: "",
  city_id: "",
  city: "",
  pincode: "",
};

const SchoolDetailsPopup = ({
  school,
  isVisible,
  onClose,
  onSchoolUpdated,
}) => {
  const roleIds = useSelector((state) => state.auth?.mdm?.role_ids || []);
  const canEdit = canUserEditSchool(roleIds);

  const [fetchSchoolEditState] = useLazyGetSchoolEditStateQuery();
  const [updateSchool, { isLoading: isUpdating }] = useUpdateSchoolMutation();
  const [getCountries] = useGetCountriesMutation();
  const [getStates] = useGetStatesMutation();
  const [getDistricts] = useGetDistrictsMutation();
  const [getCities] = useGetCitiesMutation();
  const [getCity] = useGetCityMutation();
  const [validatePincode] = useValidatePincodeMutation();

  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [originalFormData, setOriginalFormData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [schoolEditState, setSchoolEditState] = useState(null);
  const [isLoadingEditState, setIsLoadingEditState] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [isLoadingGeo, setIsLoadingGeo] = useState(false);
  const [isValidatingPincode, setIsValidatingPincode] = useState(false);
  const formDataRef = useRef({});
  const lastPincodeValidationRef = useRef({ key: "", error: null });
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  const editableFields = getEditableFields(schoolEditState);
  const isEditDisabled = isEditButtonDisabled(schoolEditState);
  const hasChanges = hasFormChanges(formData, originalFormData, editableFields);
  const udiseError = editableFields.includes("udise_code")
    ? validateUdiseCode(formData.udise_code, { required: true })
    : null;
  const canSave = hasChanges && !udiseError;

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (!isVisible) {
      setIsEditMode(false);
      setFieldErrors({});
      setSchoolEditState(null);
      setShowSuccessNotification(false);
      setShowErrorNotification(false);
      setNotificationMessage("");
    }
  }, [isVisible]);

  useEffect(() => {
    if (school) {
      const initialData = createFormDataFromSchool(school);
      setFormData(initialData);
      setOriginalFormData(initialData);
      setIsEditMode(false);
      setFieldErrors({});
    }
  }, [school]);

  useEffect(() => {
    if (!isVisible || !school?.school_id) return;

    const loadSchoolEditState = async () => {
      setIsLoadingEditState(true);
      try {
        const response = await fetchSchoolEditState(school.school_id).unwrap();
        setSchoolEditState(response.school_edit_state);
      } catch {
        setSchoolEditState(SCHOOL_EDIT_STATES.READ_ONLY);
      } finally {
        setIsLoadingEditState(false);
      }
    };

    loadSchoolEditState();
  }, [isVisible, school?.school_id, fetchSchoolEditState]);

  const loadCountries = useCallback(async () => {
    try {
      const data = await getCountries().unwrap();
      const options = mapGeoOptions(data);
      setCountryOptions(options);
      return options;
    } catch {
      setCountryOptions([]);
      return [];
    }
  }, [getCountries]);

  const loadStates = useCallback(
    async (countryId) => {
      if (!countryId) {
        setStateOptions([]);
        return [];
      }

      try {
        const data = await getStates({ countryId }).unwrap();
        const options = mapGeoOptions(data);
        setStateOptions(options);
        return options;
      } catch {
        setStateOptions([]);
        return [];
      }
    },
    [getStates],
  );

  const loadDistricts = useCallback(
    async (countryId, stateId) => {
      if (!countryId || !stateId) {
        setDistrictOptions([]);
        return [];
      }

      try {
        const data = await getDistricts({ countryId, stateId }).unwrap();
        const options = mapGeoOptions(data);
        setDistrictOptions(options);
        return options;
      } catch {
        setDistrictOptions([]);
        return [];
      }
    },
    [getDistricts],
  );

  const loadCities = useCallback(
    async (districtId) => {
      if (!districtId) {
        setCityOptions([]);
        return [];
      }

      try {
        const data = await getCities({ districtId }).unwrap();
        const options = mapGeoOptions(data);
        setCityOptions(options);
        return options;
      } catch {
        setCityOptions([]);
        return [];
      }
    },
    [getCities],
  );

  const resolveCityById = useCallback(
    async (cityId) => {
      if (!cityId) return null;

      try {
        return await getCity({ cityId }).unwrap();
      } catch {
        return null;
      }
    },
    [getCity],
  );

  const resolveGeoSelections = useCallback(
    async (data) => {
      const countries = countryOptions.length
        ? countryOptions
        : await loadCountries();

      const countryOption = data.country_id
        ? countries.find((option) => option.value === String(data.country_id))
        : findGeoOptionByName(countries, data.country);

      if (!countryOption) {
        return data;
      }

      const next = {
        ...data,
        country_id: countryOption.value,
        country: countryOption.name,
      };

      const states = await loadStates(countryOption.value);
      const stateOption = data.state_id
        ? states.find((option) => option.value === String(data.state_id))
        : findGeoOptionByName(states, data.state);

      if (!stateOption) {
        return next;
      }

      next.state_id = stateOption.value;
      next.state = stateOption.name;

      const districts = await loadDistricts(
        countryOption.value,
        stateOption.value,
      );

      const cityDetails = data.city_id
        ? await resolveCityById(data.city_id)
        : null;

      const districtOption =
        (data.district_id &&
          districts.find(
            (option) => option.value === String(data.district_id),
          )) ||
        findGeoOptionByName(districts, data.district) ||
        findDistrictForCityId(districts, data.city_id) ||
        findGeoOptionByName(districts, data.city);

      if (!districtOption) {
        return next;
      }

      next.district_id = districtOption.value;
      next.district = districtOption.name;

      const cities = await loadCities(districtOption.value);
      const cityOption =
        (data.city_id &&
          cities.find((option) => option.value === String(data.city_id))) ||
        (cityDetails?.city_id &&
          cities.find(
            (option) => option.value === String(cityDetails.city_id),
          )) ||
        findGeoOptionByName(cities, cityDetails?.city_name) ||
        findGeoOptionByName(cities, data.city) ||
        (cities.length === 1 ? cities[0] : null);

      if (cityOption) {
        next.city_id = cityOption.value;
        next.city = cityOption.name;
      }

      return next;
    },
    [
      countryOptions,
      loadCountries,
      loadStates,
      loadDistricts,
      loadCities,
      resolveCityById,
    ],
  );

  const validatePincodeWithApi = useCallback(
    async (districtId, pincode) => {
      const key = `${districtId}-${pincode}`;
      if (lastPincodeValidationRef.current.key === key) {
        return lastPincodeValidationRef.current.error;
      }

      try {
        const response = await validatePincode({
          districtId,
          pincode,
        }).unwrap();

        const validationError = getPincodeValidationError(response);
        lastPincodeValidationRef.current = { key, error: validationError };
        return validationError;
      } catch (error) {
        const message = extractApiErrorMessage(
          error,
          "Invalid pincode for selected city",
        );
        lastPincodeValidationRef.current = { key, error: message };
        return message;
      }
    },
    [validatePincode],
  );

  const runPincodeValidation = useCallback(
    async (pincode, districtId, cityId, countryName) => {
      const config = getPincodeConfig(countryName);
      const digits = String(pincode ?? "").replace(/\D/g, "");

      if (!digits) {
        lastPincodeValidationRef.current = { key: "", error: null };
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.pincode;
          return next;
        });
        return;
      }

      if (!cityId) {
        setFieldErrors((prev) => ({
          ...prev,
          pincode: "Please select a city first",
        }));
        return;
      }

      if (!districtId) {
        setFieldErrors((prev) => ({
          ...prev,
          pincode: "Please select a district first",
        }));
        return;
      }

      if (digits.length < config.length) {
        setFieldErrors((prev) => ({
          ...prev,
          pincode: config.incompleteLabel(config.length - digits.length),
        }));
        return;
      }

      setIsValidatingPincode(true);
      try {
        const apiError = await validatePincodeWithApi(districtId, digits);
        setFieldErrors((prev) => {
          const next = { ...prev };
          if (apiError) {
            next.pincode = apiError;
          } else {
            delete next.pincode;
          }
          return next;
        });
      } finally {
        setIsValidatingPincode(false);
      }
    },
    [validatePincodeWithApi],
  );

  const handleUdiseChange = useCallback((event) => {
    const raw = String(event?.target?.value ?? event ?? "");
    const nextUdise = raw.replace(/\D/g, "").slice(0, UDISE_CODE_LENGTH);

    setFormData((prev) => ({ ...prev, udise_code: nextUdise }));

    const error = validateUdiseCode(nextUdise, { required: true });
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (error) {
        next.udise_code = error;
      } else {
        delete next.udise_code;
      }
      return next;
    });
  }, []);

  const handleEmailChange = useCallback((event) => {
    const value = event?.target?.value ?? event;
    setFormData((prev) => ({ ...prev, email: value }));

    const error = value.trim()
      ? validateEmailField(value, { required: false })
      : null;

    setFieldErrors((prev) => {
      const next = { ...prev };
      if (error) {
        next.email = error;
      } else {
        delete next.email;
      }
      return next;
    });
  }, []);

  const handlePincodeChange = useCallback(
    async (event) => {
      const raw = String(event?.target?.value ?? event ?? "");
      const prev = formDataRef.current;
      const config = getPincodeConfig(prev.country);
      const nextPincode = raw.replace(/\D/g, "").slice(0, config.length);

      setFormData((current) => ({ ...current, pincode: nextPincode }));

      await runPincodeValidation(
        nextPincode,
        prev.district_id,
        prev.city_id,
        prev.country,
      );
    },
    [runPincodeValidation],
  );

  const handleFieldChange = useCallback(
    (fieldKey) => (event) => {
      const value = event?.target?.value ?? event;
      setFormData((prev) => ({ ...prev, [fieldKey]: value }));
      setFieldErrors((prev) => {
        if (!prev[fieldKey]) return prev;
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
    },
    [],
  );

  const handleGeoChange = useCallback(
    (fieldKey) => (event) => {
      const selectedId = String(event?.target?.value ?? event ?? "");
      const optionsByField = {
        country: countryOptions,
        state: stateOptions,
        district: districtOptions,
        city: cityOptions,
      };
      const selected = optionsByField[fieldKey]?.find(
        (option) => option.value === selectedId,
      );

      setFormData((prev) => {
        if (fieldKey === "country") {
          return {
            ...prev,
            country_id: selected?.value || "",
            country: selected?.name || "",
            ...EMPTY_GEO_FIELDS,
          };
        }

        if (fieldKey === "state") {
          return {
            ...prev,
            state_id: selected?.value || "",
            state: selected?.name || "",
            district_id: "",
            district: "",
            city_id: "",
            city: "",
            pincode: "",
          };
        }

        if (fieldKey === "district") {
          return {
            ...prev,
            district_id: selected?.value || "",
            district: selected?.name || "",
            city_id: "",
            city: "",
            pincode: "",
          };
        }

        return {
          ...prev,
          city_id: selected?.value || "",
          city: selected?.name || "",
          pincode: "",
        };
      });

      lastPincodeValidationRef.current = { key: "", error: null };

      if (fieldKey === "country") {
        setStateOptions([]);
        setDistrictOptions([]);
        setCityOptions([]);
        if (selected?.value) {
          loadStates(selected.value);
        }
      } else if (fieldKey === "state") {
        setDistrictOptions([]);
        setCityOptions([]);
        if (selected?.value) {
          loadDistricts(formDataRef.current.country_id, selected.value);
        }
      } else if (fieldKey === "district") {
        setCityOptions([]);
        if (selected?.value) {
          loadCities(selected.value);
        }
      }

      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        if (fieldKey === "country") {
          delete next.state;
          delete next.district;
          delete next.city;
          delete next.pincode;
        } else if (fieldKey === "state") {
          delete next.district;
          delete next.city;
          delete next.pincode;
        } else if (fieldKey === "district") {
          delete next.city;
          delete next.pincode;
        } else if (fieldKey === "city") {
          delete next.pincode;
        }
        return next;
      });
    },
    [
      countryOptions,
      stateOptions,
      districtOptions,
      cityOptions,
      loadStates,
      loadDistricts,
      loadCities,
    ],
  );

  const handleEditClick = useCallback(async () => {
    if (!canEdit || isEditDisabled) return;

    setIsEditMode(true);
    const existingUdiseError = editableFields.includes("udise_code")
      ? validateUdiseCode(formData.udise_code, { required: true })
      : null;
    setFieldErrors(existingUdiseError ? { udise_code: existingUdiseError } : {});
    setShowErrorNotification(false);
    setIsLoadingGeo(true);

    try {
      const resolved = await resolveGeoSelections(formData);
      setFormData(resolved);
      setOriginalFormData((prev) => ({
        ...prev,
        country_id: resolved.country_id || prev.country_id,
        country: resolved.country || prev.country,
        state_id: resolved.state_id || prev.state_id,
        state: resolved.state || prev.state,
        district_id: resolved.district_id || prev.district_id,
        district: resolved.district || prev.district,
        city_id: resolved.city_id || prev.city_id,
        city: resolved.city || prev.city,
      }));
      if (resolved.district_id && resolved.city_id && resolved.pincode) {
        await runPincodeValidation(
          resolved.pincode,
          resolved.district_id,
          resolved.city_id,
          resolved.country,
        );
      }
    } finally {
      setIsLoadingGeo(false);
    }
  }, [canEdit, isEditDisabled, resolveGeoSelections, formData, runPincodeValidation, editableFields]);

  const handleCancel = useCallback(() => {
    setFormData(originalFormData);
    setIsEditMode(false);
    setFieldErrors({});
    setShowErrorNotification(false);
    setStateOptions([]);
    setDistrictOptions([]);
    setCityOptions([]);
  }, [originalFormData]);

  const handleSave = useCallback(async () => {
    const errors = validateSchoolEditForm(formData, editableFields);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (
      editableFields.includes("pincode") &&
      formData.district_id &&
      formData.city_id &&
      formData.pincode
    ) {
      const apiError = await validatePincodeWithApi(
        formData.district_id,
        String(formData.pincode).replace(/\D/g, ""),
      );
      if (apiError) {
        setFieldErrors({ pincode: apiError });
        return;
      }
    }

    const payload = buildUpdateSchoolPayload(
      formData,
      originalFormData,
      editableFields,
    );

    try {
      const response = await updateSchool(payload).unwrap();

      const updatedFields = response.updated_fields || [];
      const updatedValues = Object.fromEntries(
        updatedFields.map((field) => [field, payload[field]]),
      );

      setOriginalFormData((prev) => ({ ...prev, ...updatedValues }));
      setFormData((prev) => ({ ...prev, ...updatedValues }));
      setIsEditMode(false);
      setFieldErrors({});
      setNotificationMessage(response.message);
      setShowSuccessNotification(true);
      onSchoolUpdated?.({ ...updatedValues, school_id: payload.school_id });
    } catch (error) {
      const message =
        error?.data?.message ||
        error?.data?.detail ||
        error?.message ||
        "Failed to update school";
      setNotificationMessage(message);
      setShowErrorNotification(true);
    }
  }, [
    formData,
    originalFormData,
    editableFields,
    updateSchool,
    onSchoolUpdated,
    validatePincodeWithApi,
  ]);

  const getFieldDisplayValue = useCallback(
    (field) => {
      if (field.formatDisplay) {
        return field.formatDisplay(school);
      }
      const source =
        field.alwaysReadOnly || field.viewOnly
          ? school
          : isEditMode
            ? formData
            : school;
      const value = source[field.key];
      return value || "N/A";
    },
    [formData, isEditMode, school],
  );

  const renderEditableField = (field) => {
    const editable = isFieldEditable(field.key, schoolEditState, isEditMode);
    const error =
      field.key === "udise_code" ? udiseError || fieldErrors.udise_code : fieldErrors[field.key];
    const isGeoField = Boolean(field.geoOptionsKey);

    if (!editable) {
      return (
        <div className="detail-item">
          <label>{field.label}</label>
          <span>{getFieldDisplayValue(field)}</span>
        </div>
      );
    }

    const geoOptionsMap = {
      countryOptions,
      stateOptions,
      districtOptions,
      cityOptions,
    };

    const geoDisabled =
      (field.key === "state" && !formData.country_id) ||
      (field.key === "district" && !formData.state_id) ||
      (field.key === "city" && !formData.district_id);

    const pincodeDisabled = field.key === "pincode" && !formData.city_id;

    const geoPlaceholder = geoDisabled
      ? field.key === "state"
        ? "Select country first"
        : field.key === "district"
          ? "Select state first"
          : "Select district first"
      : field.placeholder;

    const pincodePlaceholder = formData.country
      ? formData.city_id
        ? `Enter ${getPincodeConfig(formData.country).length}-digit pincode`
        : "Select city first"
      : field.placeholder;

    const inputConfig = {
      label: field.label,
      type: field.type,
      placeholder: field.key === "pincode" ? pincodePlaceholder : geoPlaceholder,
      required: field.required,
      disabled: geoDisabled || pincodeDisabled,
      ...(field.options ? { options: field.options } : {}),
      ...(isGeoField
        ? { options: geoOptionsMap[field.geoOptionsKey] || [] }
        : {}),
    };

    const fieldValue = isGeoField
      ? formData[GEO_FIELD_ID_MAP[field.key]] || ""
      : formData[field.key] || "";

    const fieldOnChange =
      field.key === "udise_code"
        ? handleUdiseChange
        : field.key === "email"
          ? handleEmailChange
          : field.key === "pincode"
            ? handlePincodeChange
            : isGeoField
              ? handleGeoChange(field.key)
              : handleFieldChange(field.key);

    return (
      <div className="detail-item detail-item--editable">
        <InputComponent
          config={inputConfig}
          value={fieldValue}
          onChange={fieldOnChange}
        />
        {error && <span className="field-error">{error}</span>}
        {field.key === "pincode" && isValidatingPincode && (
          <span className="field-error field-error--info">
            Validating pincode...
          </span>
        )}
      </div>
    );
  };

  const renderBasicDetails = () => (
    <div className="school-details-section">
      <h3 className="section-title">Basic Details</h3>
      <div className="details-grid">
        {BASIC_DETAIL_FIELDS.filter(
          (field) => !field.viewOnly || !isEditMode,
        ).map((field) => (
          <React.Fragment key={field.key}>
            {field.alwaysReadOnly || field.viewOnly ? (
              <div className="detail-item">
                <label>{field.label}</label>
                <span>{getFieldDisplayValue(field)}</span>
              </div>
            ) : (
              renderEditableField(field)
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderContactDetails = () => {
    const renderContactInfo = (contact, title) => {
      if (!contact) return null;

      return (
        <div className="contact-info-section">
          <h4>{title}</h4>
          <div className="details-grid">
            <div className="detail-item">
              <label>Name</label>
              <span>{contact.name || "N/A"}</span>
            </div>
            <div className="detail-item">
              <label>Phone</label>
              <span>
                {contact.phone ? `${contact.isd_code} ${contact.phone}` : "N/A"}
              </span>
            </div>
            <div className="detail-item">
              <label>Email</label>
              <span>{contact.email || "N/A"}</span>
            </div>
            {contact.designation && (
              <div className="detail-item">
                <label>Designation</label>
                <span>{contact.designation}</span>
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="school-details-section">
        <h3 className="section-title">Contact Details</h3>
        {renderContactInfo(school.primary_contact_info, "Primary Contact")}
        {renderContactInfo(school.secondary_contact_info, "Secondary Contact")}
      </div>
    );
  };

  const renderHeadcount = () => {
    const totalCounts = [
      { label: "Total Students", value: school.total_student_count },
      { label: "Total Teachers", value: school.total_teacher_count },
      { label: "Other Staff", value: school.other_staff_count },
    ];

    const formatGradeLabel = (key) => {
      if (key === "lkg") return "LKG";
      if (key === "ukg") return "UKG";
      return key
        .split("_")
        .map((word) => (word === "grade" ? "Grade" : word))
        .join(" ");
    };

    return (
      <div className="school-details-section">
        <h3 className="section-title">School Headcount</h3>
        <div className="details-grid">
          {totalCounts.map((count, index) => (
            <div key={index} className="detail-item">
              <label>{count.label}</label>
              <span>{count.value || 0}</span>
            </div>
          ))}
        </div>
        <h4>Grade-wise Student Count</h4>
        <div className="grade-count-grid">
          {school.grade_wise_count &&
            Object.entries(school.grade_wise_count).map(([grade, count]) => (
              <div key={grade} className="grade-count-item">
                <label>{formatGradeLabel(grade)}</label>
                <span>{count || 0}</span>
              </div>
            ))}
        </div>
      </div>
    );
  };

  const popupButtons = isEditMode
    ? {
        FirstBtnTitle: "Cancel",
        SecondBtnTitle: isUpdating ? "Saving..." : "Save",
        handelSecondry: handleCancel,
        handelPrimery: handleSave,
        primaryButtonDisabled: isUpdating || !canSave,
      }
    : canEdit
      ? {
          FirstBtnTitle: "Close",
          SecondBtnTitle: "Edit",
          handelSecondry: onClose,
          handelPrimery: handleEditClick,
          primaryButtonDisabled: isLoadingEditState || isEditDisabled,
        }
      : {
          FirstBtnTitle: "Close",
          handelSecondry: onClose,
        };

  if (!school) return null;

  return (
    <>
      <Popup
        isVisible={isVisible}
        onClose={onClose}
        title="School Details"
        contentClassName="school-details-popup__content"
        {...popupButtons}
      >
        <div className="school-details-popup__body">
          {isLoadingEditState && canEdit && (
            <div className="school-details-popup__loader">
              <img src={loaderGif} alt="Loading..." />
            </div>
          )}
          {isLoadingGeo && isEditMode && (
            <div className="school-details-popup__loader">
              <img src={loaderGif} alt="Loading location options..." />
            </div>
          )}
          {renderBasicDetails()}
          {!isEditMode && (
            <>
              {renderContactDetails()}
              {renderHeadcount()}
            </>
          )}
        </div>
      </Popup>

      {showSuccessNotification && (
        <NotificationPortal>
          <div
            style={{ ...notificationStyle, borderLeft: "8px solid #008000" }}
          >
            <button
              type="button"
              onClick={() => setShowSuccessNotification(false)}
              style={notificationCloseStyle}
              aria-label="Dismiss success notification"
            >
              <img
                src={closeIcon}
                alt=""
                style={{ width: "16px", height: "16px" }}
              />
            </button>
            <h3
              style={{
                color: "#008000",
                margin: "0 0 8px 0",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              Update Successful
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#273743",
                fontWeight: "400",
                margin: 0,
              }}
            >
              {notificationMessage}
            </p>
          </div>
        </NotificationPortal>
      )}

      {showErrorNotification && (
        <NotificationPortal>
          <div
            style={{ ...notificationStyle, borderLeft: "8px solid #ff0000" }}
          >
            <button
              type="button"
              onClick={() => setShowErrorNotification(false)}
              style={notificationCloseStyle}
              aria-label="Dismiss error notification"
            >
              <img
                src={closeIcon}
                alt=""
                style={{ width: "16px", height: "16px" }}
              />
            </button>
            <h3
              style={{
                color: "#ff0000",
                margin: "0 0 8px 0",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              Update Failed
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#273743",
                fontWeight: "400",
                margin: 0,
              }}
            >
              {notificationMessage}
            </p>
          </div>
        </NotificationPortal>
      )}
    </>
  );
};

export default SchoolDetailsPopup;
